import { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { anunciosService, uploadService } from '../services/api'
import { useAuth } from '../contexts/AuthContext'
import './CriarAnuncio.css' // Reutilizando estilos

function EditarAnuncio() {
    const { id } = useParams()
    const navigate = useNavigate()
    const { usuario, isLogado } = useAuth()
    const [submitting, setSubmitting] = useState(false)
    const [loading, setLoading] = useState(true)
    const [erro, setErro] = useState('')
    const [uploading, setUploading] = useState(false)
    const [showSuccessModal, setShowSuccessModal] = useState(false)

    const [form, setForm] = useState({
        titulo: '',
        descricao: '',
        preco: '',
        quantidade: '',
        peso: '',
        unidadePeso: 'kg',
        idade: '',
        unidadeIdade: 'meses',
        raca: 'Nelore',
        genero: 'Macho',
        categoria: 'Bovino',
        nomeFazenda: '',
        cidade: '',
        estado: '',
        imagens: ['', '', '']
    })

    // Raças por categoria (Mesma lista do CriarAnuncio)
    const racasPorCategoria = {
        Bovino: ['Nelore', 'Angus', 'Brahman', 'Brangus', 'Senepol', 'Girolando', 'Holandês', 'Jersey', 'Outra'],
        Equino: ['Crioulo', 'Quarto de Milha', 'Mangalarga', 'Campolina', 'Árabe', 'Outra'],
        Suíno: ['Landrace', 'Large White', 'Duroc', 'Pietrain', 'Outra'],
        Ovino: ['Dorper', 'Santa Inês', 'Texel', 'Ille de France', 'Outra'],
        Caprino: ['Boer', 'Saanen', 'Anglo-Nubiana', 'Outra'],
        Outros: ['Outra']
    }

    const estados = [
        { sigla: 'AC', nome: 'Acre' }, { sigla: 'AL', nome: 'Alagoas' }, { sigla: 'AP', nome: 'Amapá' },
        { sigla: 'AM', nome: 'Amazonas' }, { sigla: 'BA', nome: 'Bahia' }, { sigla: 'CE', nome: 'Ceará' },
        { sigla: 'DF', nome: 'Distrito Federal' }, { sigla: 'ES', nome: 'Espírito Santo' }, { sigla: 'GO', nome: 'Goiás' },
        { sigla: 'MA', nome: 'Maranhão' }, { sigla: 'MT', nome: 'Mato Grosso' }, { sigla: 'MS', nome: 'Mato Grosso do Sul' },
        { sigla: 'MG', nome: 'Minas Gerais' }, { sigla: 'PA', nome: 'Pará' }, { sigla: 'PB', nome: 'Paraíba' },
        { sigla: 'PR', nome: 'Paraná' }, { sigla: 'PE', nome: 'Pernambuco' }, { sigla: 'PI', nome: 'Piauí' },
        { sigla: 'RJ', nome: 'Rio de Janeiro' }, { sigla: 'RN', nome: 'Rio Grande do Norte' }, { sigla: 'RS', nome: 'Rio Grande do Sul' },
        { sigla: 'RO', nome: 'Rondônia' }, { sigla: 'RR', nome: 'Roraima' }, { sigla: 'SC', nome: 'Santa Catarina' },
        { sigla: 'SP', nome: 'São Paulo' }, { sigla: 'SE', nome: 'Sergipe' }, { sigla: 'TO', nome: 'Tocantins' }
    ]

    useEffect(() => {
        carregarAnuncio()
    }, [id])

    const carregarAnuncio = async () => {
        try {
            const data = await anunciosService.buscarPorId(id)
            const anuncio = data.anuncio

            // Verificar permissão
            if (usuario && anuncio.vendedor?.id !== usuario.id) {
                alert('Você não tem permissão para editar este anúncio.')
                navigate('/meus-anuncios')
                return
            }

            // Mapear dados do backend (snake_case) para o form (camelCase)
            setForm({
                titulo: anuncio.titulo,
                descricao: anuncio.descricao,
                preco: anuncio.preco,
                quantidade: anuncio.quantidade,
                peso: anuncio.peso || anuncio.pesoMedio || '',
                unidadePeso: anuncio.unidadePeso || 'kg',
                idade: anuncio.idade || anuncio.idadeMeses || '',
                unidadeIdade: anuncio.unidadeIdade || 'meses',
                raca: anuncio.raca,
                genero: anuncio.genero || 'Macho',
                categoria: anuncio.categoria,
                nomeFazenda: anuncio.nomeFazenda || '',
                cidade: anuncio.cidade,
                estado: anuncio.estado,
                imagens: anuncio.imagens && anuncio.imagens.length > 0 ? anuncio.imagens : [anuncio.imagem || '']
            })
        } catch (error) {
            console.error('Erro ao carregar anúncio:', error)
            setErro('Erro ao carregar dados do anúncio.')
        } finally {
            setLoading(false)
        }
    }

    const handleInputChange = (e) => {
        const { name, value } = e.target
        
        setForm(prev => {
            const nextForm = { ...prev, [name]: value };
            
            // Se mudou a categoria, reseta a raça para a primeira da nova categoria
            if (name === 'categoria') {
                const novasRacas = racasPorCategoria[value] || ['Outra'];
                nextForm.raca = novasRacas[0];
            }
            
            return nextForm;
        });
    }

    const handleFileUpload = async (e) => {
        const files = Array.from(e.target.files)
        if (files.length === 0) return

        setUploading(true)
        setErro('')

        try {
            const novasImagens = [...form.imagens].filter(img => img && img !== '')

            for (const file of files) {
                const isVideoFile = file.type.startsWith('video/');
                const limitMB = isVideoFile ? 100 : 10;
                if (file.size > limitMB * 1024 * 1024) {
                    throw new Error(`Arquivo ${file.name} passou do limite de ${limitMB}MB. Por favor, envie um arquivo menor.`);
                }
                const response = await uploadService.uploadImagem(file)
                novasImagens.push(response.url)
            }

            // Reorganizar para que os vídeos fiquem em primeiro
            const isVideo = url => url.match(/\.(mp4|mov|avi|webm)$/i) || url.includes('/video/upload/');
            novasImagens.sort((a, b) => isVideo(b) - isVideo(a));

            setForm(prev => ({ ...prev, imagens: novasImagens }))
        } catch (error) {
            setErro(error.message || 'Erro ao fazer upload.')
        } finally {
            setUploading(false)
            e.target.value = ''
        }
    }

    const removeImage = (indexToRemove) => {
        setForm(prev => ({
            ...prev,
            imagens: prev.imagens.filter((_, index) => index !== indexToRemove)
        }))
    }

    const handleSubmit = async (e) => {
        e.preventDefault()
        setSubmitting(true)
        setErro('')

        try {
            const imagensValidas = form.imagens.filter(img => img && img.trim() !== '')

            if (!form.titulo || !form.preco || !form.quantidade || !form.cidade || !form.estado) {
                throw new Error('Preencha todos os campos obrigatórios.')
            }

            const anuncioData = {
                ...form,
                imagens: imagensValidas,
                imagem: imagensValidas[0] || '', // Capa

                // Mapeamento inverso para snake_case (se o backend esperar assim, 
                // mas geralmente a API trata. Vou mandar camelCase e ver se o backend aceita,
                // ou mapear manualmente se necessário. O CriarAnuncio mandava camelCase no form...
                // Vou mandar as chaves que o backend espera no UPDATE)

                pesoMedio: form.peso,
                idadeMeses: form.idade,
                unidadePeso: form.unidadePeso,
                unidadeIdade: form.unidadeIdade,
                nome_fazenda: form.nomeFazenda
            }

            await anunciosService.atualizar(id, anuncioData)
            setShowSuccessModal(true)

        } catch (error) {
            setErro(error.message)
            window.scrollTo(0, 0)
        } finally {
            setSubmitting(false)
        }
    }

    if (loading) return <div className="loading">Carregando...</div>

    return (
        <div className="criar-anuncio-page">
            <div className="criar-anuncio-container">
                <header className="criar-anuncio-header">
                    <h1>Editar Anúncio</h1>
                    <p>Atualize os detalhes do seu anúncio</p>
                </header>

                {erro && <div className="alert alert-error">{erro}</div>}

                <form onSubmit={handleSubmit}>
                    {/* Seção 1: Dados do Animal */}
                    <div className="form-section">
                        <h3>{form.categoria === 'Outros' ? '📦 Detalhes do Produto/Máquina' : '🐮 Detalhes do Animal'}</h3>
                        <div className="form-grid">
                            <div className="form-group full-width">
                                <label className="form-label">Título do Anúncio *</label>
                                <input type="text" name="titulo" className="form-input" value={form.titulo} onChange={handleInputChange} required />
                            </div>

                            <div className="form-group">
                                <label className="form-label">Categoria</label>
                                <select name="categoria" className="form-select" value={form.categoria} onChange={handleInputChange}>
                                    <option value="Bovino">Bovino</option>
                                    <option value="Equino">Equino</option>
                                    <option value="Suíno">Suíno</option>
                                    <option value="Ovino">Ovino</option>
                                    <option value="Outros">Outros</option>
                                </select>
                            </div>

                            {form.categoria !== 'Outros' && (
                                <>
                                    <div className="form-group">
                                        <label className="form-label">Raça *</label>
                                        <select name="raca" className="form-select" value={form.raca} onChange={handleInputChange}>
                                            {racasPorCategoria[form.categoria]?.map(raca => (
                                                <option key={raca} value={raca}>{raca}</option>
                                            ))}
                                        </select>
                                    </div>

                                    <div className="form-group">
                                        <label className="form-label">Gênero</label>
                                        <select name="genero" className="form-select" value={form.genero} onChange={handleInputChange}>
                                            <option value="Macho">Macho</option>
                                            <option value="Fêmea">Fêmea</option>
                                        </select>
                                    </div>
                                </>
                            )}

                            <div className="form-group">
                                <label className="form-label">Quantidade *</label>
                                <input type="number" name="quantidade" className="form-input" value={form.quantidade} onChange={handleInputChange} required />
                            </div>

                            {form.categoria !== 'Outros' && (
                                <div className="form-grid" style={{ gridColumn: 'span 1', display: 'flex', gap: '10px' }}>
                                    <div className="form-group" style={{ flex: 1 }}>
                                        <label className="form-label">Peso Médio</label>
                                        <input
                                            type="number"
                                            name="peso"
                                            className="form-input"
                                            placeholder={form.unidadePeso === 'kg' ? "Ex: 450" : "Ex: 15"}
                                            value={form.peso}
                                            onChange={handleInputChange}
                                        />
                                    </div>
                                    <div className="form-group" style={{ width: '100px' }}>
                                        <label className="form-label">Unidade</label>
                                        <select name="unidadePeso" className="form-select" value={form.unidadePeso} onChange={handleInputChange}>
                                            <option value="kg">kg</option>
                                            <option value="@">@ (Arr)</option>
                                        </select>
                                    </div>
                                </div>
                            )}

                            {form.categoria !== 'Outros' && (
                                <div className="form-grid" style={{ gridColumn: 'span 1', display: 'flex', gap: '10px' }}>
                                    <div className="form-group" style={{ flex: 1 }}>
                                        <label className="form-label">Idade</label>
                                        <input type="number" name="idade" className="form-input" value={form.idade} onChange={handleInputChange} />
                                    </div>
                                    <div className="form-group" style={{ width: '100px' }}>
                                        <label className="form-label">Unidade</label>
                                        <select name="unidadeIdade" className="form-select" value={form.unidadeIdade} onChange={handleInputChange}>
                                            <option value="meses">Meses</option>
                                            <option value="anos">Anos</option>
                                        </select>
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="form-group full-width" style={{ marginTop: '1rem' }}>
                            <label className="form-label">Descrição Completa</label>
                            <textarea name="descricao" className="form-textarea" value={form.descricao} onChange={handleInputChange}></textarea>
                        </div>
                    </div>

                    {/* Seção 2: Localização */}
                    <div className="form-section">
                        <h3>📍 Localização</h3>
                        <div className="form-grid">
                            <div className="form-group full-width">
                                <label className="form-label">Nome da Fazenda *</label>
                                <input type="text" name="nomeFazenda" className="form-input" value={form.nomeFazenda} onChange={handleInputChange} required />
                            </div>
                            <div className="form-group">
                                <label className="form-label">Cidade *</label>
                                <input type="text" name="cidade" className="form-input" value={form.cidade} onChange={handleInputChange} required />
                            </div>
                            <div className="form-group">
                                <label className="form-label">Estado *</label>
                                <select name="estado" className="form-select" value={form.estado} onChange={handleInputChange} required>
                                    <option value="">Selecione</option>
                                    {estados.map(est => (
                                        <option key={est.sigla} value={est.sigla}>{est.nome}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    </div>

                    {/* Seção 3: Valor e Fotos */}
                    <div className="form-section">
                        <h3>💰 Valor e Fotos</h3>
                        <div className="form-grid">
                            <div className="form-group full-width">
                                <label className="form-label">Valor (R$) *</label>
                                <input 
                                    type="number" 
                                    name="preco" 
                                    className="form-input" 
                                    value={form.preco} 
                                    onChange={handleInputChange} 
                                    onWheel={(e) => e.target.blur()}
                                    required 
                                />
                            </div>

                            <div className="form-group full-width">
                                <label className="form-label">Fotos e Vídeos (Mínimo 1, max 50MB)</label>
                                <div className="upload-container">
                                    <input type="file" id="edit-file-upload" multiple accept="image/*,video/mp4,video/mov,video/avi,video/webm" onChange={handleFileUpload} style={{ display: 'none' }} disabled={uploading} />
                                    <label htmlFor="edit-file-upload" className={`btn btn-outline btn-block ${uploading ? 'disabled' : ''}`} style={{ cursor: 'pointer', textAlign: 'center' }}>
                                        {uploading ? '⏳ Enviando...' : '📷 Adicionar Fotos/Vídeos'}
                                    </label>
                                </div>

                                <div className="image-preview-container" style={{ marginTop: '1rem' }}>
                                    {form.imagens.map((img, index) => {
                                        if (!img) return null;
                                        const isVideo = img.match(/\.(mp4|mov|avi|webm)$/i) || img.includes('/video/upload/');
                                        return (
                                            <div key={index} className="image-preview-item">
                                                {isVideo ? (
                                                    <video src={img} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                                ) : (
                                                    <img src={img} alt={`Mídia ${index}`} />
                                                )}
                                                <button type="button" className="btn-remove-image" onClick={() => removeImage(index)}>×</button>
                                            </div>
                                        )
                                    })}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="form-actions">
                        <button type="button" className="btn btn-secondary btn-block" onClick={() => navigate('/meus-anuncios')}>Cancelar</button>
                        <button type="submit" className="btn btn-primary btn-block" disabled={submitting}>
                            {submitting ? 'Salvando...' : 'Salvar Alterações'}
                        </button>
                    </div>
                </form>
                {showSuccessModal && (
                    <div style={{
                        position: 'fixed',
                        top: 0, left: 0, right: 0, bottom: 0,
                        background: 'rgba(15, 23, 42, 0.75)',
                        backdropFilter: 'blur(8px)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 1000
                    }}>
                        <div style={{
                            background: '#1e293b',
                            border: '1px solid rgba(255, 255, 255, 0.1)',
                            padding: '2.5rem',
                            borderRadius: '20px',
                            maxWidth: '400px',
                            width: '90%',
                            textAlign: 'center',
                            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)'
                        }}>
                            <div style={{
                                width: '60px',
                                height: '60px',
                                background: 'rgba(34, 197, 94, 0.15)',
                                color: '#22c55e',
                                borderRadius: '50%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '1.8rem',
                                margin: '0 auto 1.25rem auto'
                            }}>
                                ✔
                            </div>
                            <h3 style={{ color: '#f8fafc', fontSize: '1.35rem', fontWeight: '700', marginBottom: '0.5rem' }}>
                                Sucesso!
                            </h3>
                            <p style={{ color: '#94a3b8', marginBottom: '1.75rem', fontSize: '0.9rem', lineHeight: '1.5' }}>
                                Seu anúncio foi atualizado com sucesso no sistema.
                            </p>
                            <button 
                                onClick={() => navigate('/meus-anuncios')}
                                className="btn btn-primary btn-block"
                                style={{ margin: 0 }}
                            >
                                Entendido
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    )
}

export default EditarAnuncio
